// E-1 — sceneGraph.ts: PURE geometry→scene-graph function (blueprint §5.1).
// Zero React imports. Converts LiftDetail geometry (+ LiftComputed) into a
// scene graph of nodes in SCENE FEET (x = ft from tower centerline, side B
// negative; y = ft above grade), segments, and annotations (angle arcs,
// dimensions, labels). pxPerFt is the SINGLE scale factor for BOTH axes —
// letterboxed to the bounding box of ALL points (incl. tag anchor and side-B
// extents); never distorted, no vertical exaggeration.
import type {
  LiftDetail, LiftComputed, Pt, StructureType,
} from '@/types/planData';
import {
  solveStaticTagSegments,
} from './riggingCalculations.v2';
import type { SegmentForces } from '@/types/planData';

export type PointKind =
  | 'topBlock' | 'heelBlock' | 'jumpBlock' | 'redirectBlock' | 'mountBlock'
  | 'tieBackAnchor' | 'winch' | 'load' | 'tagAnchor' | 'staticUpperAnchor'
  | 'holdPoint' | 'trolley' | 'highlineShaftAnchor' | 'obstruction' | 'slingEnd';

export interface ScenePoint { id: string; pt: Pt; kind: PointKind }

export interface SceneSegment {
  id: string; from: string; to: string;
  kind: 'ropePart' | 'tagLine' | 'staticLine' | 'highline' | 'tieBack' | 'slingLeg' | 'crossing';
  style: 'solid' | 'dashed';
  parts?: number; // n parallel lines, A-13(e) style
}

export interface SceneAnnotation {
  id: string;
  kind: 'angleArc' | 'dimension' | 'label' | 'limitArc' | 'forceArrow' | 'refusalMarker';
  at: Pt;
  text?: string;
  valueDeg?: number;
  state?: 'ok' | 'fail';
}

export interface Scene {
  points: ScenePoint[];
  segments: SceneSegment[];
  annotations: SceneAnnotation[];
  structure: { type: StructureType; heightFt: number; faceWidthFt: number };
  boundingBoxFt: { minX: number; maxX: number; minY: number; maxY: number };
}

const deg = (rad: number) => (rad * 180) / Math.PI;
const angleFromVertical = (dx: number, dy: number) => deg(Math.atan2(Math.abs(dx), Math.abs(dy)));

// ---------------------------------------------------------------------------

export function buildScene(lift: LiftDetail, computed: LiftComputed): Scene {
  const g = lift.geometry;
  const points: ScenePoint[] = [];
  const segments: SceneSegment[] = [];
  const annotations: SceneAnnotation[] = [];

  const sideX = (side: 'A' | 'B', dist: number) => (side === 'A' ? dist : -dist);

  // --- structure nodes -------------------------------------------------------
  const top: Pt = { x: g.topBlock.offsetFromCenterFt, y: g.topBlock.heightFt };
  points.push({ id: 'topBlock', pt: top, kind: 'topBlock' });

  const heel = g.heelBlock
    ? { x: g.heelBlock.offsetFromTowerFt, y: g.heelBlock.heightFt }
    : null;
  if (heel) points.push({ id: 'heelBlock', pt: heel, kind: 'heelBlock' });

  const jump = g.jumpBlock
    ? { x: g.jumpBlock.offsetFromCenterFt, y: g.jumpBlock.heightFt }
    : null;
  if (jump) points.push({ id: 'jumpBlock', pt: jump, kind: 'jumpBlock' });

  const redirect = g.redirectBlock
    ? { x: g.redirectBlock.offsetFt, y: g.redirectBlock.heightFt }
    : null;
  if (redirect) points.push({ id: 'redirectBlock', pt: redirect, kind: 'redirectBlock' });

  const mount = g.mountBlock
    ? { x: g.mountBlock.offsetFt, y: g.mountBlock.heightFt }
    : null;
  if (mount) points.push({ id: 'mountBlock', pt: mount, kind: 'mountBlock' });

  if (g.tieBackAnchor) {
    points.push({ id: 'tieBackAnchor', pt: { x: top.x, y: g.tieBackAnchor.heightFt }, kind: 'tieBackAnchor' });
  }

  if (g.highline) {
    const shaft: Pt = { x: g.highline.shaftAnchor.offsetFt, y: g.highline.shaftAnchor.heightFt };
    points.push({ id: 'highlineShaftAnchor', pt: shaft, kind: 'highlineShaftAnchor' });
    // trolley position = linear interpolation along the span (fraction)
    const t: Pt = {
      x: shaft.x + (mount ? mount.x - shaft.x : 0) * g.highline.trolleyPositionFt / Math.max(1, g.highline.trolleyPositionFt + g.load.distanceFromTowerFt),
      y: shaft.y + ((mount ? mount.y : top.y) - shaft.y) * g.highline.trolleyPositionFt / Math.max(1, g.highline.trolleyPositionFt + g.load.distanceFromTowerFt),
    };
    points.push({ id: 'trolley', pt: t, kind: 'trolley' });
  }

  const winch: Pt = { x: sideX(g.winch.side, g.winch.distanceFromTowerFt), y: g.winch.heightFt };
  points.push({ id: 'winch', pt: winch, kind: 'winch' });

  const load: Pt = { x: g.load.distanceFromTowerFt, y: g.load.heightFt };
  points.push({ id: 'load', pt: load, kind: 'load' });

  // tag anchor + hold point — SAME side as the winch (Brian field ruling 2026-09-08:
  // the tagger works the load side, which is the winch side)
  const tagSide: 'A' | 'B' = g.winch.side;
  let tagAnchor: Pt | null = null;
  // E-4 tag taxonomy: static = trolley on the dashed STATIC line (load line does
  // NOT reeve through it); self/selfTrolley = trolley ON the LOAD line.
  let trolleyOnLoadLine = false;
  if (g.tag.method === 'straight' && g.tag.anchorDistanceFt != null) {
    tagAnchor = { x: sideX(tagSide, g.tag.anchorDistanceFt), y: g.tag.anchorHeightFt ?? 0 };
    points.push({ id: 'tagAnchor', pt: tagAnchor, kind: 'tagAnchor' });
  } else if (g.tag.method === 'self' || g.tag.method === 'selfTrolley') {
    // Self tag: NO separate tag line / ground hold point — the load rides the
    // load line on a trolley block (Brian 2026-09-08: "self tag and trolley
    // tag are the same thing"). Trolley is placed on the load line below.
    trolleyOnLoadLine = true;
  } else if (g.tag.method === 'staticTrolley') {
    tagAnchor = { x: sideX(tagSide, g.tag.anchorDistanceFt ?? 40), y: g.tag.anchorHeightFt ?? 0 };
    points.push({ id: 'tagAnchor', pt: tagAnchor, kind: 'tagAnchor' });
    if (g.tag.staticUpperAnchorHeightFt != null) {
      points.push({ id: 'staticUpperAnchor', pt: { x: top.x, y: g.tag.staticUpperAnchorHeightFt }, kind: 'staticUpperAnchor' });
    }
  }

  // trolley node: staticTrolley → rides the dashed STATIC line; self/selfTrolley
  // → rides the LOAD line (taxonomy per E-4).
  if (g.tag.method === 'staticTrolley') {
    const upper = g.tag.staticUpperAnchorHeightFt ?? top.y;
    const standoff = g.tag.loadStandoffFt ?? Math.abs(load.x);
    const t: Pt = {
      x: sideX(tagSide, Math.max(1, standoff)),
      y: upper + (0 - upper) * (Math.abs(tagAnchor ? tagAnchor.x - (top.x + sideX(tagSide, standoff)) : standoff) / Math.max(1, Math.abs(tagAnchor ? tagAnchor.x - top.x : 1))),
    };
    // simple linear interpolation along the static line at the standoff x
    const anchorX = tagAnchor ? tagAnchor.x : sideX(tagSide, 40);
    const frac = Math.abs((anchorX - t.x) / (anchorX - top.x || 1));
    t.y = upper + (tagAnchor ? tagAnchor.y : 0) - upper - (tagAnchor ? tagAnchor.y - upper : -upper) * frac;
    points.push({ id: 'trolley', pt: t, kind: 'trolley' });
  } else if (trolleyOnLoadLine) {
    // Trolley rides the LOAD LINE between the top block and the load — placed
    // a visible distance above the load so the self-tag depiction is clearly
    // different from a straight tag (Brian sketch 2026-09-08: winch → block on
    // tower → line down through trolley block → load hangs from the trolley).
    const spanY = Math.max(top.y - load.y, 1e-6);
    const drop = Math.min(6, spanY * 0.15); // 6 ft or 15% of the span above the load
    const fracAlong = drop / spanY;
    const t: Pt = {
      x: load.x + (top.x - load.x) * fracAlong,
      y: load.y + (top.y - load.y) * fracAlong,
    };
    points.push({ id: 'trolley', pt: t, kind: 'trolley' });
  }

  // Sling legs are NOT drawn in the tower elevation — a 2-leg sling from the top
  // block to the load visually split the load line into 3 phantom loads. The
  // block-attachment slings (1-leg vs face-basket 2-leg) get their own inset
  // diagram (FaceBasketInset) beside the tower view.

  // --- segments (per-scenario routing, §5.1 item 6) ---------------------------
  const parts = lift.hardware.blockParts;
  const seg = (id: string, from: string, to: string, kind: SceneSegment['kind'], style: SceneSegment['style'] = 'solid', nParts?: number) =>
    segments.push({ id, from, to, kind, style, parts: nParts });

  const isS3 = lift.scenario === 'S3-jump';

  if (isS3 && jump) {
    // S3: winch(B) → up B → jump → crossing (dashed over tower) → top(A) → load
    seg('winch-jump', 'winch', 'jumpBlock', 'ropePart');
    seg('crossing', 'topBlock', 'jumpBlock', 'crossing', 'dashed');
    seg('top-load', 'topBlock', 'load', 'ropePart', 'solid');
    annotations.push({ id: 'jump-callout', kind: 'label', at: jump, text: 'JUMP' });
  } else if (lift.scenario === 'S4-tied-back' && g.tieBackAnchor) {
    seg('winch-top', 'winch', 'topBlock', 'ropePart');
    seg('top-load', 'topBlock', 'load', 'ropePart');
    seg('tieback', 'topBlock', 'tieBackAnchor', 'tieBack');
    const delta = Math.abs(g.topBlock.heightFt - g.tieBackAnchor.heightFt);
    annotations.push({
      id: 'tieback-dim',
      kind: 'dimension',
      at: { x: top.x + 1, y: (top.y + g.tieBackAnchor.heightFt) / 2 },
      text: `${delta} FT — 4 FEET OR LESS (≤ 4 FT ✓)`,
      state: delta <= 4 ? 'ok' : 'fail',
    });
    if (delta > 4) {
      annotations.push({
        id: 'tieback-dim-warn',
        kind: 'dimension',
        at: { x: top.x + 1, y: (top.y + g.tieBackAnchor.heightFt) / 2 - 3 },
        text: `WARNING — EXCEEDS 4 FT (§4.4.4.1)`,
        state: 'fail',
      });
    }
  } else if (lift.scenario === 'S5-redirect' && redirect && mount) {
    seg('winch-redirect', 'winch', 'redirectBlock', 'ropePart');
    seg('redirect-mount', 'redirectBlock', 'mountBlock', 'ropePart');
    seg('mount-load', 'mountBlock', 'load', 'ropePart');
    annotations.push({ id: 'mount-callout', kind: 'label', at: mount, text: 'MOUNT BLOCK' });
    annotations.push({ id: 'redirect-callout', kind: 'label', at: redirect, text: 'IDLER/REDIRECT BLOCK' });
    // A-4(a) S5 primary-block variant label set
    annotations.push({ id: 'a4a-load-line', kind: 'label', at: { x: (mount.x + load.x) / 2, y: (mount.y + load.y) / 2 }, text: 'LOAD LINE' });
    annotations.push({ id: 'a4a-to-hoist', kind: 'label', at: { x: (winch.x + redirect.x) / 2, y: (winch.y + redirect.y) / 2 }, text: 'TO HOIST' });
    annotations.push({ id: 'a4a-heel', kind: 'label', at: { x: 0, y: redirect.y - 2 }, text: 'HEEL BLOCK' });
    // ≤4 ft dimension (§4.4.4.1): mount/tie-back attach to the top block attach
    const deltaFt = Math.abs(mount.y - redirect.y);
    annotations.push({
      id: 'a4a-4ft-dim',
      kind: 'dimension',
      at: { x: mount.x + 1, y: (mount.y + redirect.y) / 2 },
      text: `${deltaFt.toFixed(0)} FT — 4 FEET OR LESS`,
      state: deltaFt <= 4 ? 'ok' : 'fail',
    });
  } else if (lift.scenario === 'S6-highline-trolley' && g.highline && mount) {
    const shaft = points.find((p) => p.id === 'highlineShaftAnchor')!;
    const trolley = points.find((p) => p.id === 'trolley')!;
    seg('highline-span', 'highlineShaftAnchor', 'mountBlock', 'highline');
    seg('load-line-through', 'topBlock', 'load', 'ropePart');
    seg('trolley-load', 'trolley', 'load', 'slingLeg');
    annotations.push({ id: 'hl-span-label', kind: 'label', at: shaft.pt, text: 'REDUNDANT RIGGING SLING — HIGHLINE' });
    void trolley;
  } else {
    // S1 / S2: winch → [heel] → top → load
    if (heel) {
      seg('winch-heel', 'winch', 'heelBlock', 'ropePart');
      seg('heel-top', 'heelBlock', 'topBlock', 'ropePart');
    } else {
      seg('winch-top', 'winch', 'topBlock', 'ropePart');
    }
    seg('top-load', 'topBlock', 'load', 'ropePart', 'solid', parts);
  }
  // tag line — dashed, labeled TAG LINE (straight tag runs load → ground hold
  // point on the winch side; self tag has NO separate line — the trolley rides
  // the load line; static trolley: upper anchor → trolley → ground hold point)
  if (tagAnchor) {
    if (g.tag.method === 'straight') {
      seg('tag-line', 'load', 'tagAnchor', 'tagLine', 'dashed');
    } else if (g.tag.method === 'staticTrolley') {
      if (g.tag.staticUpperAnchorHeightFt != null) {
        seg('static-upper', 'staticUpperAnchor', 'trolley', 'staticLine', 'dashed');
        seg('tag-line', 'trolley', 'tagAnchor', 'staticLine', 'dashed');
      } else {
        seg('tag-line', 'load', 'tagAnchor', 'staticLine', 'dashed');
      }
    }
    // self/selfTrolley: no separate tag line by definition
  }

  // --- component call-out labels (Brian 2026-09-08: "more detailed like the
  // screenshot — you can see each labeled component") ------------------------
  // straight tag: the dashed line IS the tag line — label it at mid-span.
  if (g.tag.method === 'straight' && tagAnchor) {
    annotations.push({
      id: 'tag-line-label',
      kind: 'label',
      at: { x: (load.x + tagAnchor.x) / 2 + 2, y: (load.y + tagAnchor.y) / 2 },
      text: 'TAG LINE',
    });
  }
  // self tag: trolley block on the load line; load hangs from it on a short line.
  if (trolleyOnLoadLine) {
    seg('trolley-load', 'trolley', 'load', 'slingLeg');
    annotations.push({ id: 'self-tag-note', kind: 'label', at: { x: load.x + 3, y: load.y + 10 }, text: 'LOAD RIDES LOAD LINE VIA TROLLEY BLOCK' });
  }
  // top block sling (how the block hangs on the tower)
  annotations.push({ id: 'sling-label', kind: 'label', at: { x: top.x + 2, y: top.y + 4 }, text: 'SLING' });
  // load line label on the main top→load span (skip S5/S6 which label their own spans)
  if (lift.scenario === 'S1-top-only' || lift.scenario === 'S2-top-heel') {
    annotations.push({
      id: 'load-line-label',
      kind: 'label',
      at: { x: (top.x + load.x) / 2 + 2, y: (top.y + load.y) / 2 + 6 },
      text: 'LOAD LINE',
    });
  }
  // hoist line label: winch→(heel)→top
  if (lift.scenario === 'S1-top-only' || lift.scenario === 'S2-top-heel') {
    const midFrom = heel ?? winch;
    annotations.push({
      id: 'hoist-line-label',
      kind: 'label',
      at: { x: (midFrom.x + (heel ? heel.x : top.x)) / 2, y: (midFrom.y + (heel ? heel.y : top.y)) / 2 - 4 },
      text: heel ? 'HOIST LINE' : 'HOIST LINE',
    });
  }

  // E-4: staticTrolley segment bands + obstruction + governing-band callout
  // (SEG-PATH). Segment forces come from the engine's solveStaticTagSegments;
  // when LiftComputed.segments is absent (computeLift doesn't run the segment
  // path for plain staticTrolley S1), re-solve here from geometry — same
  // engine function, diagram never invents numbers.
  if (g.tag.method === 'staticTrolley') {
    const segForces = computed.forces.segments;
    let solved: SegmentForces[] | null = null;
    if (segForces && segForces.length > 0) solved = segForces;
    else if (g.tag.standoffSegments && g.tag.standoffSegments.length > 0) {
      try {
        const r = solveStaticTagSegments({
          W: lift.loading.grossLoadLbs * lift.loading.dynamicFactor,
          topBlockHt: g.topBlock.heightFt,
          staticUpperAnchorHt: g.tag.staticUpperAnchorHeightFt ?? g.topBlock.heightFt,
          holdPointFt: g.tag.anchorDistanceFt ?? 40,
          segments: g.tag.standoffSegments.map((s) => ({ fromElevationFt: s.fromElevationFt, toElevationFt: s.toElevationFt, standoffFt: s.standoffFt, note: s.note })),
        });
        solved = r.refusals.length === 0 ? r.segments : null;
      } catch { solved = null; }
    }
    if (solved && solved.length > 0) {
      const gov = computed.forces.governingSegmentIndex ?? 0;
      for (const s of solved) {
        annotations.push({
          id: `seg-band-${s.fromElevationFt}`,
          kind: 'label',
          at: { x: sideX(tagSide, s.standoffFt), y: (s.fromElevationFt + s.toElevationFt) / 2 },
          text: `SEG ${s.fromElevationFt}–${s.toElevationFt} FT · STANDOFF ${s.standoffFt} FT`,
        });
      }
      const s = solved[Math.min(gov, solved.length - 1)];
      annotations.push({
        id: 'governing-band',
        kind: 'label',
        at: { x: sideX(tagSide, s.standoffFt + 6), y: (s.fromElevationFt + s.toElevationFt) / 2 },
        text: `GOVERNING SEGMENT ${s.fromElevationFt}–${s.toElevationFt} FT`,
        state: 'fail',
      });
    }
  }
  if (g.tag.method === 'staticTrolley' && g.tag.obstruction) {
    const ob = g.tag.obstruction;
    annotations.push({
      id: 'obstruction-marker',
      kind: 'label',
      at: { x: sideX(tagSide, (g.tag.loadStandoffFt ?? 4) + ob.clearanceFt), y: ob.heightFt },
      text: `OBSTRUCTION — ${ob.type.toUpperCase()} @ ${ob.heightFt} FT, CLEAR ${ob.clearanceFt} FT`,
      state: 'fail',
    });
  }

  // E-4: 10°/70° dotted limit arcs on straight-tag geometry (IV(f) triggers)
  if (g.tag.method === 'straight') {
    for (const lim of [10, 70]) {
      annotations.push({
        id: `limit-arc-${lim}`,
        kind: 'limitArc',
        at: load,
        valueDeg: lim,
        text: `${lim}° LIMIT (IV(f))`,
      });
    }
  }

  // --- annotations: angle arcs (FROM VERTICAL convention stated) --------------
  const thetaDeg = computed.derivedAngles.loadPositionDeg;
  annotations.push({
    id: 'theta-arc',
    kind: 'angleArc',
    at: load,
    valueDeg: thetaDeg,
    text: `θ = ${thetaDeg.toFixed(2)}° FROM VERTICAL`,
  });

  if (heel && computed.forces.heelBlockLbs != null) {
    // φ at the heel: angle between heel→top and heel→winch
    const u1 = unit(heel, top);
    const u2 = unit(heel, winch);
    const phi = deg(Math.acos(clamp(u1.x * u2.x + u1.y * u2.y, -1, 1)));
    annotations.push({ id: 'phi-arc', kind: 'angleArc', at: heel, valueDeg: phi, text: `φ = ${phi.toFixed(1)}°` });
    // fleet arc at the top: deviation of top→heel from vertical
    const fleetDeg = computed.derivedAngles.fleetAngleDeg ?? angleFromVertical(top.x - heel.x, top.y - heel.y);
    annotations.push({
      id: 'fleet-arc',
      kind: 'angleArc',
      at: top,
      valueDeg: fleetDeg,
      text: `FLEET ${fleetDeg.toFixed(2)}°`,
      state: fleetDeg > 5 ? 'fail' : fleetDeg > 2 ? undefined : 'ok',
    });
  }

  // one gravity arrow off the load + weight label
  annotations.push({ id: 'gravity-arrow', kind: 'forceArrow', at: load, text: `LOAD ${lift.loading.grossLoadLbs} LB` });
  annotations.push({
    id: 'from-vertical-note',
    kind: 'label',
    at: { x: 0, y: 0 },
    text: 'ALL ANGLES MEASURED FROM VERTICAL',
  });

  // force callouts (label annotations, no color — force overlay is E-3)
  if (computed.status === 'refused' && computed.refusals.length > 0) {
    annotations.push({
      id: 'refusal-marker',
      kind: 'refusalMarker',
      at: top,
      text: `NO SOLUTION — ${computed.refusals[0].rule}`,
      state: 'fail',
    });
  } else {
    annotations.push({
      id: 'top-force',
      kind: 'label',
      at: top,
      text: `TOP BLOCK ${computed.forces.topBlockResultantLbs.toFixed(1)} LB`,
    });
  }

  // --- bounding box: ALL points (incl. tag anchor + side-B extents) -----------
  const xs = points.map((p) => p.pt.x);
  const ys = points.map((p) => p.pt.y);
  // Silhouette extents (reference-styled art): antenna mast above the top,
  // lattice base spread, guyed anchors far to the sides — all must fit in frame.
  const halfW = (g.faceWidthFt ?? 4) / 2;
  const silTop = g.structureHeightFt * 1.08; // antenna mast cap
  let silSide = halfW * 3 + 6;               // self-support base plates
  if (g.structureType === 'guyed') {
    const maxGuyY = Math.min(g.structureHeightFt - 1, Math.floor((g.structureHeightFt - 1) / 50) * 50);
    silSide = Math.max(silSide, halfW + maxGuyY * 0.6 + 4);
  } else if (g.structureType === 'monopole' || g.structureType === 'mast-pipe') {
    silSide = halfW * 1.6 + 2;
  }
  const boundingBoxFt = {
    minX: Math.min(...xs, -silSide),
    maxX: Math.max(...xs, silSide),
    minY: Math.min(...ys, -2),
    maxY: Math.max(...ys, silTop),
  };

  return {
    points,
    segments,
    annotations,
    structure: {
      type: g.structureType,
      heightFt: g.structureHeightFt,
      faceWidthFt: g.faceWidthFt ?? 4,
    },
    boundingBoxFt,
  };
}

// pxPerFt — SINGLE factor for BOTH axes from the bounding box of ALL points.
// Letterboxed into the viewport (84% usable margin); the binding axis fills it
// exactly; the other axis is centered (letterbox). Never distorts.
export function pxPerFt(scene: Scene, viewportW: number, viewportH: number): number {
  const spanX = scene.boundingBoxFt.maxX - scene.boundingBoxFt.minX;
  const spanY = scene.boundingBoxFt.maxY - scene.boundingBoxFt.minY;
  const usableW = viewportW * 0.84;
  const usableH = viewportH * 0.84;
  return Math.min(usableW / Math.max(spanX, 1e-9), usableH / Math.max(spanY, 1e-9));
}

// --- helpers -----------------------------------------------------------------
function unit(from: Pt, to: Pt): Pt {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  return { x: dx / len, y: dy / len };
}
function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}
